// =============================================================================
// src/views/perfil-regiao/index.ts — Secao 02 "Perfil da Regiao" (AppModule + SectionController).
//
// Proveniencia: Sections.perfilRegiao (assets/unified-app.js 1050-1137) +
// marcacao #section-perfil-regiao (unified.html 391-568).
//
// Esta view cumpre DOIS contratos de types.ts:
//   - AppModule        -> render() (marcacao) + init(ctx) (listeners pos-montagem).
//   - SectionController -> activate(map) / deactivate() (comportamento de mapa por scroll-spy).
// Ambos sao expostos para a integracao (main.ts) plugar como precisar.
//
// Fidelidade: highlight Norte / dim demais, flyTo centroide de Norte, bind de
// tabs e sliders, e o reset de estilo + fitBairros ao sair — identicos ao original.
// =============================================================================

import type {
  AppContext,
  AppModule,
  MapController,
  SectionController,
  ZonaKey,
  ZoneStyleFn,
} from '../../app/types';
import { renderPerfilRegiaoSection } from './perfil-regiao-markup';
import { bindPerfilTabs } from './perfil-regiao-tabs';
import { bindPerfilBelonging } from './perfil-regiao-belonging';
import { ensurePerfilInsights } from './perfil-regiao-insights';

/**
 * Campos de runtime do MapController usados pela Secao 02 que NAO estao no
 * contrato publico MapController (types.ts). Proveniencia: assets/unified-app.js
 * acessa `mapCtrl.zoneCentroids` (1066-1068) e `MapController.currentZoneStyleFn`
 * (1134). Modelados aqui como opcionais para acesso defensivo, preservando
 * comportamento sem ampliar o contrato compartilhado.
 */
interface MapControllerRuntimeExtras {
  zoneCentroids?: Partial<Record<ZonaKey, [number, number]>>;
  currentZoneStyleFn?: ZoneStyleFn | null;
}

/** Estilo da Zona Norte em destaque (FIEL: unified-app.js 1060). */
function perfilNorteHighlightStyle(): ReturnType<ZoneStyleFn> {
  return { fillColor: '#1D9E75', fillOpacity: 0.82, weight: 2, color: '#0F6E56', opacity: 1 };
}

/** Estilo das demais zonas, esmaecidas (FIEL: unified-app.js 1062). */
function perfilZoneDimStyle(): ReturnType<ZoneStyleFn> {
  return {
    fillColor: '#1D9E75',
    fillOpacity: 0.18,
    weight: 0.8,
    color: 'rgba(255,255,255,0.3)',
    opacity: 0.5,
  };
}

/** Funcao de estilo passada a setZoneStyles: Norte destacada, resto esmaecido. */
function perfilZoneStyleFn(zonaKey: ZonaKey): ReturnType<ZoneStyleFn> {
  if (zonaKey === 'norte') return perfilNorteHighlightStyle();
  return perfilZoneDimStyle();
}

/** Voa para o centroide de Norte se o MapController expuser zoneCentroids (FIEL 1066-1068). */
function flyToNorteCentroid(mapCtrl: MapController): void {
  const centroids = (mapCtrl as MapController & MapControllerRuntimeExtras).zoneCentroids;
  const norteCentroid = centroids?.norte;
  if (norteCentroid) mapCtrl.flyTo(norteCentroid, 13, 1.2);
}

/**
 * Controlador da Secao 02. Mantem o estado do original (overlays/insightsInjected),
 * embora insightsInjected so vire true via ensurePerfilInsights quando acionado.
 */
class PerfilRegiaoController implements SectionController {
  /** Overlays de mapa criados pela secao (espelha o array do original; nao usado nesta secao). */
  readonly overlays: readonly string[] = [];
  /** Flag de injecao unica dos cards de insight (espelha Sections.perfilRegiao.insightsInjected). */
  private insightsInjected = false;
  /** MapController da activate mais recente, consumido por deactivate (FIEL ao singleton original). */
  private lastMapCtrl: MapController | null = null;

  /**
   * Entrada na secao (scroll-spy): destaca Norte, voa ao centroide e liga listeners.
   * Proveniencia: Sections.perfilRegiao.activate (1055-1072).
   */
  activate(mapCtrl: MapController): void {
    mapCtrl.setZoneStyles(perfilZoneStyleFn);
    flyToNorteCentroid(mapCtrl);
    bindPerfilTabs();
    bindPerfilBelonging();
  }

  /**
   * Injeta os cards de insight do Copiloto uma unica vez (opt-in da integracao).
   * Espelha _ensureInsights; isolado de activate como no original (la era chamado
   * pelo Copiloto, nao por activate).
   */
  ensureInsights(): void {
    this.insightsInjected = ensurePerfilInsights(this.insightsInjected);
  }

  /**
   * Saida da secao: limpa a funcao de estilo de zona e reenquadra os bairros.
   * Proveniencia: Sections.perfilRegiao.deactivate (1133-1136) — opera sobre o
   * MapController capturado na ultima activate.
   */
  deactivate(): void {
    if (this.lastMapCtrl) {
      (this.lastMapCtrl as MapController & MapControllerRuntimeExtras).currentZoneStyleFn = null;
      this.lastMapCtrl.fitBairros();
    }
  }

  /** Registra o MapController ativo; chamado por activate antes de aplicar estilos. */
  rememberMapCtrl(mapCtrl: MapController): void {
    this.lastMapCtrl = mapCtrl;
  }
}

const perfilRegiaoController = new PerfilRegiaoController();

/**
 * AppModule da Secao 02. render() emite a marcacao FIEL; init(ctx) liga os
 * listeners de tabs e sliders apos o DOM montado e, se o mapa ja existir,
 * aplica o comportamento de activate.
 */
export const perfilRegiaoView: AppModule = {
  id: 'perfil-regiao',

  /** Marcacao literal de #section-perfil-regiao (paridade com unified.html). */
  render(): string {
    return renderPerfilRegiaoSection();
  },

  /**
   * Pos-montagem: liga SO os listeners de DOM (tabs/sliders), que sao one-time e
   * independem da secao estar visivel. A ativacao do mapa fica a cargo do
   * SectionManager (scroll-spy), igual ao unified.html — que ativa apenas
   * mapa-eleitoral no boot. Ativar aqui fazia o perfil-regiao mexer no mapa
   * compartilhado fora de vista. Fix 2026-05-31.
   */
  init(ctx: AppContext): void {
    bindPerfilTabs();
    bindPerfilBelonging();
    if (ctx.map) perfilRegiaoController.rememberMapCtrl(ctx.map);
  },
};

/**
 * Controlador exposto para a integracao acoplar o scroll-spy (activate/deactivate)
 * e a injecao de insights do Copiloto. activate registra o MapController antes de
 * aplicar estilos, garantindo que deactivate opere sobre o mesmo controlador.
 */
export const perfilRegiaoSection: SectionController & {
  ensureInsights(): void;
} = {
  activate(mapCtrl: MapController): void {
    perfilRegiaoController.rememberMapCtrl(mapCtrl);
    perfilRegiaoController.activate(mapCtrl);
  },
  deactivate(): void {
    perfilRegiaoController.deactivate();
  },
  ensureInsights(): void {
    perfilRegiaoController.ensureInsights();
  },
};
